import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Button } from "../../../../components/Button";
import {
  type ScholarshipClass,
  type ScholarshipPlayer,
} from "../../../../types/scholarships";
import type { ScholarshipDetailContext } from "./ScholarshipDetailLayout";
import {
  isPositionFilter,
  type PositionFilter,
} from "./positionGroups";
import { useRosterSport } from "./useRosterSport";
import { useRosterPlayers } from "./useRosterPlayers";
import { rosterSettingsOf } from "./rosterPalette";
import { RosterLegend } from "./RosterLegend";
import { RosterSpotsCount } from "./RosterSpotsCount";
import { tourAnchor } from "../../../tours/tourAnchors";
import { useTour } from "../../../tours/useTour";
import { isScholarshipClass } from "./scholarshipClasses";
import { matchesRosterFilters } from "./rosterFilters";
import { ScholarshipPlayerModal } from "./ScholarshipPlayerModal";
import { ProspectQuickViewPanel } from "../players/ProspectQuickViewPanel";
import { openableProspectId } from "./openableProspect";
import { ScholarshipFiltersModal } from "./ScholarshipFiltersModal";
import { bucketPlayersIntoCards } from "./depthChartPositions";
import { DepthChartDiamond } from "./DepthChartDiamond";
import { PitchingStaffSection } from "./PitchingStaffSection";
import { bucketPitchersByThrows } from "./pitchingStaff";
import {
  FILTER_PARAM_KEYS,
  loadDepthChartDisplayMode,
  loadDepthChartView,
  loadDepthChartZoom,
  saveDepthChartView,
  loadScholarshipFilters,
  saveDepthChartDisplayMode,
  saveDepthChartZoom,
  saveScholarshipFilters,
  type DepthChartDisplayMode,
  type DepthChartView,
} from "./scholarshipFilterStorage";
import { DepthChartZoomControl } from "./DepthChartZoomControl";
import { resolveDepthChartView } from "./depthChartLayout";
import { DIAMOND_VIEWBOX } from "./depthChartPositions";
import { useElementWidth } from "../../../../hooks/useElementWidth";
import { clampZoom, ZOOM_FIT } from "./depthChartZoom";
import { formatMoney } from "./scholarshipMoney";
import "./depthChart.css";
import { isRecruitingBoard } from "./boardPurpose";
import "./scholarshipPlayers.css";

// The diamond + pitching staff cover all 12 real positions; 'Other' (null
// position) has no card here, so it's excluded from the filter list to avoid the
// dead-end where a filter shows a non-zero count but the tab reports "no
// players match these filters."
const DEPTH_CHART_POSITION_FILTERS: PositionFilter[] = [
  "RHP",
  "LHP",
  "C",
  "1B",
  "2B",
  "3B",
  "SS",
  "LF",
  "CF",
  "RF",
  "DH",
  "UTIL",
];

const parsePositions = (raw: string | null): PositionFilter[] => {
  if (!raw) return [];
  return raw.split(",").filter(isPositionFilter);
};

const parseClasses = (raw: string | null): ScholarshipClass[] => {
  if (!raw) return [];
  return raw.split(",").filter(isScholarshipClass);
};

export const ScholarshipDepthChartTab = () => {
  const { scholarship, refresh: refreshScholarship } =
    useOutletContext<ScholarshipDetailContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  // Reorder here re-sorts the rows optimistically and rolls back on failure, exactly like the
  // Players tab; a 409 refetches the board.
  const {
    players,
    error,
    refresh: refreshPlayers,
    reorder,
    mergeSaved,
  } = useRosterPlayers(scholarship.id);
  const settings = rosterSettingsOf(scholarship);
  const [modalPlayer, setModalPlayer] = useState<ScholarshipPlayer | null>(
    null
  );
  /** The attached player whose profile is open in the side panel. */
  const [viewProspectId, setViewProspectId] = useState<string | null>(null);

  // Filters start collapsed on the depth chart (full width for the diamond);
  // once the coach toggles, the persisted choice wins for the session.
  // A modal has no open/closed state worth persisting — it is opened for a moment and
  // dismissed, unlike the panel it replaces, whose collapsed state had to survive a reload.
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Global Totals/Average toggle for every card on the depth chart. Defaults to totals.
  const [displayMode, setDisplayMode] = useState<DepthChartDisplayMode>(
    () => loadDepthChartDisplayMode(scholarship.id) ?? "total"
  );

  const setDisplayModePersisted = (mode: DepthChartDisplayMode) => {
    setDisplayMode(mode);
    saveDepthChartDisplayMode(scholarship.id, mode);
  };

  // Save-as-PDF via the browser print dialog. The body class scopes the print
  // stylesheet to this view so printing other pages is unaffected; it's cleared
  // after the dialog closes.
  useEffect(() => {
    const clear = () => document.body.classList.remove("scholarship-printing");
    window.addEventListener("afterprint", clear);
    return () => {
      window.removeEventListener("afterprint", clear);
      clear();
    };
  }, []);

  const handlePrint = () => {
    document.body.classList.add("scholarship-printing");
    window.print();
  };

  // 'softball' → softball field art and DP labelling; anything else (including null) →
  // baseball. Shared with every other position-rendering surface via toRosterSport.
  const sport = useRosterSport();

  // Defaults to ZOOM_FIT: the measured fit alone, where the chart exactly fills the container's
  // width. The coach's zoom multiplies that fit (see depthChartZoom).
  const [zoom, setZoom] = useState<number>(
    () => clampZoom(loadDepthChartZoom(scholarship.id) ?? ZOOM_FIT),
  );

  const handleZoomChange = (next: number) => {
    setZoom(next);
    saveDepthChartZoom(scholarship.id, next);
  };

  // Field or list. With no explicit choice the chart's own width decides, so a laptop with the
  // sidebar open, or an iPad, opens on cards it can read rather than a field of truncated names.
  const [chartRef, chartWidth] = useElementWidth<HTMLDivElement>();
  const [chosenView, setChosenView] = useState(() => loadDepthChartView());
  // The automatic choice remembers what it last resolved to, so the threshold has hysteresis: a
  // scrollbar appearing or disappearing near the boundary cannot flip the layout back and forth.
  // Adjusted during render (React's pattern for state derived from a changing input) rather than
  // in an effect, so the first measured paint already shows the right layout.
  const [autoView, setAutoView] = useState<DepthChartView | null>(null);
  const view = resolveDepthChartView(chosenView, chartWidth, autoView);
  if (chosenView == null && chartWidth != null && view !== autoView) setAutoView(view);
  // The field is drawn at its design size and scaled to the chart's width, text and all, so it
  // looks the same at every width instead of squeezing cards around fixed-size text. Null until
  // measured (and in jsdom), which leaves the field sized by percentages as before. Floored, so a
  // fractional width never scales the field a sub-pixel past its box.
  const fit = chartWidth != null && chartWidth > 0
    ? Math.floor(chartWidth) / DIAMOND_VIEWBOX.width
    : null;
  const chooseView = (next: DepthChartView) => {
    setChosenView(next);
    saveDepthChartView(next);
  };

  const positionFilters = useMemo(
    () => parsePositions(searchParams.get("positions")),
    [searchParams]
  );
  const classFilters = useMemo(
    () => parseClasses(searchParams.get("classes")),
    [searchParams]
  );

  // Restore saved filters on mount when the URL has none. Deep-linking with
  // explicit params wins; this only fills in when the user came back to the
  // tab via nav and the URL is bare.
  useEffect(() => {
    const hasUrlFilters = FILTER_PARAM_KEYS.some((k) => searchParams.has(k));
    if (hasUrlFilters) return;
    const saved = loadScholarshipFilters("depthChart", scholarship.id);
    if (!saved) return;
    const next = new URLSearchParams(searchParams);
    for (const k of FILTER_PARAM_KEYS) {
      const v = saved.get(k);
      if (v) next.set(k, v);
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scholarship.id]);

  // Persist filters on every change so they survive navigation away to the
  // Players sub-tab and back.
  useEffect(() => {
    saveScholarshipFilters("depthChart", scholarship.id, searchParams);
  }, [scholarship.id, searchParams]);

  const updateFilterParam = (key: "positions" | "classes", values: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (values.length === 0) next.delete(key);
    else next.set(key, values.join(","));
    setSearchParams(next, { replace: false });
  };

  const togglePosition = (filter: PositionFilter) => {
    const next = positionFilters.includes(filter)
      ? positionFilters.filter((f) => f !== filter)
      : [...positionFilters, filter];
    updateFilterParam("positions", next);
  };

  const toggleClass = (playerClass: ScholarshipClass) => {
    const next = classFilters.includes(playerClass)
      ? classFilters.filter((c) => c !== playerClass)
      : [...classFilters, playerClass];
    updateFilterParam("classes", next);
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("positions");
    next.delete("classes");
    setSearchParams(next, { replace: false });
  };

  const filteredPlayers = useMemo(() => {
    if (players == null) return [];
    return players.filter((p) =>
      matchesRosterFilters(p, positionFilters, classFilters)
    );
  }, [players, positionFilters, classFilters]);

  const playersByCard = useMemo(
    () => bucketPlayersIntoCards(filteredPlayers),
    [filteredPlayers]
  );

  const pitchersByThrows = useMemo(
    () => bucketPitchersByThrows(filteredPlayers),
    [filteredPlayers]
  );

  const allCardsEmpty = useMemo(
    () =>
      Object.values(playersByCard).every((arr) => arr.length === 0) &&
      pitchersByThrows.RHP.length === 0 &&
      pitchersByThrows.LHP.length === 0,
    [playersByCard, pitchersByThrows]
  );
  const hasActiveFilter = positionFilters.length > 0 || classFilters.length > 0;

  // Header counts over the FILTERED set. "On scholarship" is model-aware:
  // FullHalf plans measure by assigned unit; the dollar/Equivalency plans by a
  // positive percent or award. "On money" is any NIL or rev-share dollars
  // (independent of the plan's tracking toggles — the per-player data always exists).
  const onScholarshipCount = useMemo(
    () =>
      filteredPlayers.filter((p) =>
        scholarship.scholarshipModel === "FullHalf"
          ? p.scholarshipUnit != null
          : (p.scholarshipPercent ?? 0) > 0 || (p.award ?? 0) > 0
      ).length,
    [filteredPlayers, scholarship.scholarshipModel]
  );
  const receivingMoneyCount = useMemo(
    () =>
      filteredPlayers.filter(
        (p) => (p.nilAmount ?? 0) + (p.revShare ?? 0) > 0
      ).length,
    [filteredPlayers]
  );

  /*
    A player attached from the player list opens their profile in the side panel, over the chart,
    as their name does on the Players tab. A row added by hand has no profile, so it opens the
    roster edit form as before.
  */
  const openPlayer = (player: ScholarshipPlayer) => {
    const prospectId = openableProspectId(player);
    if (prospectId) setViewProspectId(prospectId);
    else setModalPlayer(player);
  };

  const handleModalSuccess = (_cost: number | null, saved: ScholarshipPlayer | null) => {
    setModalPlayer(null);
    mergeSaved(saved);
    refreshPlayers();
    refreshScholarship();
  };

  // A second view of the roster feature: its own page tour, no data tour, and no registration
  // that would overwrite the Players tab's (see web/CLAUDE.md, Guided tours).
  useTour("rosters", {
    ready: players != null || error != null,
    hasData: filteredPlayers.length > 0,
    pageTourKey: "rosters.depth",
    dataTourKey: null,
  });

  if (players == null && !error) {
    return <div>Loading players…</div>;
  }

  const diamond = (layout: "field" | "list") => (
    <DepthChartDiamond
      sport={sport}
      playersByCard={playersByCard}
      scholarshipModel={scholarship.scholarshipModel}
      displayMode={displayMode}
      positionBudgets={scholarship.summary.positionBudgets}
      positionNeeds={scholarship.positionBudgets}
      settings={settings}
      onClickPlayer={openPlayer}
      // A filtered card shows part of a position, and the reorder endpoint takes the whole
      // position, so a drag there would be refused. Sorting waits for the filters to clear.
      onReorder={hasActiveFilter ? undefined : reorder}
      // Zoom and fit belong to the field; a list of cards is drawn at full size.
      {...(layout === "field"
        ? { zoom: (fit ?? 1) * zoom, fitted: fit != null }
        : {})}
      view={layout}
    />
  );

  return (
    <div className="scholarship-depth-chart">
      <div className="scholarship-depth-chart__main">
        {error && (
          <div className="scholarship-depth-chart__error" role="alert">
            {error}
          </div>
        )}

        {hasActiveFilter && (
          <p className="depth-chart-print-filter-note">
            Filtered view —
            {positionFilters.length > 0 && ` positions: ${positionFilters.join(", ")}`}
            {positionFilters.length > 0 && classFilters.length > 0 && ";"}
            {classFilters.length > 0 && ` classes: ${classFilters.join(", ")}`}
          </p>
        )}

        <div className="depth-chart-counts" data-testid="depth-chart-counts">
          <span className="depth-chart-counts__item">
            On scholarship:{" "}
            <strong data-testid="count-on-scholarship">{onScholarshipCount}</strong>
          </span>
          <span className="depth-chart-counts__separator" aria-hidden="true">
            ·
          </span>
          <span className="depth-chart-counts__item">
            On money:{" "}
            <strong data-testid="count-receiving-money">{receivingMoneyCount}</strong>
          </span>
          <span className="depth-chart-counts__separator" aria-hidden="true">
            ·
          </span>
          {/* A recruiting board has no roster spots setting, so no count against one. */}
          {!isRecruitingBoard(scholarship) && (
            <>
              <RosterSpotsCount scholarship={scholarship} />
              <span className="depth-chart-counts__separator" aria-hidden="true">
                ·
              </span>
            </>
          )}
          {/*
            Deliberately inconsistent with its two neighbours: they count the FILTERED set,
            this is the server's plan-wide total over every non-deleted player. Academic aid is
            reported for the roster as a whole, so re-summing filteredPlayers here would answer
            a different question and would drift from the number the API reports elsewhere. The
            tooltip states the difference rather than leaving a coach to infer it from a total
            that does not move when they filter.
          */}
          <span
            className="depth-chart-counts__item"
            title="Total academic aid across the whole roster — not affected by the filters"
          >
            Academic money:{" "}
            <strong data-testid="count-academic-money">
              {/* The field is optional in the type to model deploy skew — see
                  ScholarshipSummary.academicTotal — so this coalesce is required by the
                  compiler, not merely defensive. Without it a web build talking to an API
                  that predates the field would print "$NaN" beside two correct counts. */}
              {formatMoney(scholarship.summary.academicTotal ?? 0)}
            </strong>
            {/* The title above never reaches a keyboard or touch user, and this figure's whole
                design problem is that it behaves differently from the two beside it. */}
            <span className="depth-chart-counts__sr-only">
              {" "}
              across the whole roster, not affected by the filters
            </span>
          </span>
          {/* One cluster, so a narrow window wraps the controls as a group rather than
              leaving the zoom stranded on a line of its own. */}
          <div className="depth-chart-counts__controls">
            <div
              className="depth-chart-display-toggle depth-chart-view-toggle"
              role="group"
              aria-label="Depth chart layout"
              {...tourAnchor("rosters.depth.layout")}
            >
              <button
                type="button"
                className={`depth-chart-display-toggle__option${view === "field" ? " depth-chart-display-toggle__option--active" : ""}`}
                aria-pressed={view === "field"}
                data-testid="depth-chart-view-field"
                onClick={() => chooseView("field")}
              >
                Field
              </button>
              <button
                type="button"
                className={`depth-chart-display-toggle__option${view === "list" ? " depth-chart-display-toggle__option--active" : ""}`}
                aria-pressed={view === "list"}
                data-testid="depth-chart-view-list"
                onClick={() => chooseView("list")}
              >
                List
              </button>
            </div>
            <div
              className="depth-chart-display-toggle"
              role="group"
              aria-label="Card value display"
            >
              <button
                type="button"
                className={`depth-chart-display-toggle__option${displayMode === "total" ? " depth-chart-display-toggle__option--active" : ""}`}
                aria-pressed={displayMode === "total"}
                data-testid="display-mode-total"
                onClick={() => setDisplayModePersisted("total")}
              >
                Totals
              </button>
              <button
                type="button"
                className={`depth-chart-display-toggle__option${displayMode === "average" ? " depth-chart-display-toggle__option--active" : ""}`}
                aria-pressed={displayMode === "average"}
                data-testid="display-mode-average"
                onClick={() => setDisplayModePersisted("average")}
              >
                Average
              </button>
            </div>
            <Button
              variant="secondary"
              onClick={handlePrint}
              className="depth-chart-print-button"
              data-testid="depth-chart-print"
            >
              Save as PDF
            </Button>
            {/*
              In the same container as the toggle and Save as PDF now, rather than in a sibling
              <aside>. That aside is what made this control impossible to line up: it carried the
              filter panel's padding and border even when collapsed.
            */}
            <Button
              variant="secondary"
              onClick={() => setFiltersOpen(true)}
              data-testid="depth-chart-filters"
            >
              Filters
              {hasActiveFilter && (
                <span className="depth-chart-counts__filter-badge">
                  {positionFilters.length + classFilters.length}
                </span>
              )}
            </Button>
            {/* Zoom scales the field; a list of cards already reads at full size. */}
            {view === "field" && <DepthChartZoomControl zoom={zoom} onChange={handleZoomChange} />}
          </div>
        </div>

        {allCardsEmpty && hasActiveFilter && (
          <div className="depth-chart-empty-banner" role="status">
            <span className="depth-chart-empty-banner__message">
              No players match these filters.
            </span>
            <Button variant="secondary" onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        )}

        <div className="scholarship-depth-chart__scroll" ref={chartRef}>
          {view === "field" ? (
            <div
              className="depth-chart-zoom-frame"
              // String, not the number: a custom property is set verbatim, and leaving the
              // stringification implicit is the kind of thing a future refactor gets wrong.
              style={
                {
                  '--dc-zoom': String(zoom),
                  ...(fit != null ? { '--dc-fit': String(fit) } : {}),
                } as React.CSSProperties
              }
            >
              {diamond("field")}
            </div>
          ) : (
            diamond("list")
          )}
        </div>

        <RosterLegend
          settings={settings}
          sections={["handedness", "indicators", "traits"]}
          {...tourAnchor("rosters.depth.legend")}
        />

        <PitchingStaffSection
          recruitingBoard={isRecruitingBoard(scholarship)}
          pitchersByThrows={pitchersByThrows}
          scholarshipModel={scholarship.scholarshipModel}
          displayMode={displayMode}
          positionBudgets={scholarship.summary.positionBudgets}
          onClickPlayer={openPlayer}
        />
      </div>

      {filtersOpen && (
        <ScholarshipFiltersModal
          players={players ?? []}
          positionFilters={positionFilters}
          classFilters={classFilters}
          onTogglePosition={togglePosition}
          onToggleClass={toggleClass}
          onClear={clearFilters}
          onClose={() => setFiltersOpen(false)}
          availablePositionFilters={DEPTH_CHART_POSITION_FILTERS}
          sport={sport}
        />
      )}

      {modalPlayer && (
        <ScholarshipPlayerModal
          recruitingBoard={isRecruitingBoard(scholarship)}
          scholarshipId={scholarship.id}
          scholarshipModel={scholarship.scholarshipModel}
          mode="edit"
          existingPlayer={modalPlayer}
          rosterSettings={settings}
          sport={sport}
          onClose={() => setModalPlayer(null)}
          onSuccess={handleModalSuccess}
          onProspectChanged={() => {
            refreshPlayers();
            refreshScholarship();
          }}
        />
      )}

      <ProspectQuickViewPanel
        prospectId={viewProspectId}
        isOpen={viewProspectId !== null}
        onClose={() => setViewProspectId(null)}
        onProspectChanged={() => {
          refreshPlayers();
          refreshScholarship();
        }}
      />
    </div>
  );
};
